import React from 'react';
import { HelpingHand, CheckCircle, Target, Users, BarChart3 } from 'lucide-react';

export default function B2BSales() {
  const framework = [
    {
      phase: 'Phase 1: ICP & Value Narrative',
      duration: 'Weeks 1-2',
      activities: [
        'Ideal customer profile definition across industry, firmographic, and trigger data',
        'Buyer committee mapping with problem statements and decision criteria',
        'Value messaging architecture aligned to financial and operational outcomes',
        'Sales asset audit and gap plan for decks, one-pagers, and case stories'
      ]
    },
    {
      phase: 'Phase 2: Revenue Operations Build',
      duration: 'Weeks 3-5',
      activities: [
        'CRM instance configuration with lifecycle stages, SLAs, and automation',
        'Outbound sequence development combining email, phone, and social touches',
        'Demo and discovery scripts with objection handling and ROI modeling',
        'Pipeline governance cadences for forecast accuracy and accountability'
      ]
    },
    {
      phase: 'Phase 3: Pipeline Activation',
      duration: 'Weeks 6-10',
      activities: [
        'Account-based prospecting campaigns with curated insight hooks',
        'Partner and channel motion design for referrals and co-selling',
        'Sales enablement sprints covering negotiation and procurement alignment',
        'Revenue leadership coaching to reinforce habits and rituals'
      ]
    },
    {
      phase: 'Phase 4: Expansion & Retention',
      duration: 'Ongoing',
      activities: [
        'Customer success playbooks tied to onboarding, QBRs, and health scoring',
        'Cross-sell and upsell mapping with trigger-based outreach sequences',
        'Voice-of-customer loops fueling product roadmap and marketing proof',
        'Dashboarding that connects pipeline, revenue, and retention signals'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'Founders-to-Sellers Transition',
      icon: HelpingHand,
      description: 'We help founders institutionalize their best pitches into repeatable systems—and then coach the next hires to run them.'
    },
    {
      title: 'Deal Room Discipline',
      icon: Target,
      description: 'Every opportunity gets a mutual action plan, stakeholder scorecard, and risk tracker so momentum never stalls in procurement.'
    },
    {
      title: 'Enablement in Motion',
      icon: Users,
      description: 'Live call reviews, battle cards, and playbooks are shipped weekly so teams constantly sharpen their craft.'
    },
    {
      title: 'Revenue Intelligence',
      icon: BarChart3,
      description: 'We wire dashboards that illuminate pipeline health, cycle time, and win themes—fueling better decisions each week.'
    }
  ];

  const deliverables = [
    'ICP dossier with TAM sizing, intent signals, and account prioritization model',
    'Outbound cadences, call scripts, and enablement library ready for your team',
    'Revenue operating cadence playbook with meeting agendas and KPIs',
    '90-day hiring plan outlining sales, SDR, and success headcount sequencing'
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <HelpingHand size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">B2B Sales Acceleration</h1>
              <p className="text-xl text-gray-600 mt-2">Build a predictable revenue engine beyond founder-led sales</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Motif Unbound Revenue Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            We hardwire strategy, tooling, and team habits so B2B revenue becomes forecastable—without losing the scrappy edge that opened the first doors.
          </p>
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Why Founders Trust Us</h2>
          <p className="text-lg text-gray-700 mb-8">
            We sit inside your pipeline, joining calls, building assets, and coaching teams until the revenue rhythm sticks.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Enablement Toolkit</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <ul className="space-y-3">
              {deliverables.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Let’s Build Your B2B Revenue Engine</h2>
          <p className="text-xl mb-8 opacity-90">
            We combine process, talent, and tooling so every quarter delivers predictable growth.
          </p>
          <a
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
